import { updateCorporate } from "../../../../app/api/corporate";
import { BaseAction } from "../../../../core/actions/BaseAction";
import { getTranslatedMessage } from "../../../../Lib/Utils/i18n";
import { showNotification } from "../../../../Services/notificationService";
import {
    getRecoilStateValue,
    updateRecoilState,
} from "../../../../Orchestrator/Recoils/Events/Recoil";
import { IApiResponse } from "../../../../Orchestrator/Types/Index";
import {
    ICorporateInformation,
    ICorporateOk,
    ICorporateResponse,
} from "../../../../Orchestrator/Types/Interface/ICorporate";
import { IUserResponse } from "../../../../Orchestrator/Types/Interface/IUser";
import { getCorporateAction } from "./GetCorporateAction";

export interface IUpdateCorporateActionParams {
    /**
     * The COMPLETE Corporate Information object. Build it with
     * buildCorporateUpdateBody / buildCardRemovalBody — a partial body would
     * wipe the company's address and contract.
     */
    information: ICorporateInformation;
}

class UpdateCorporateAction extends BaseAction<
    ICorporateResponse,
    IUpdateCorporateActionParams
> {
    // Deliberately NOT deduplicated: BaseAction coalesces by run, ignoring
    // params, so a second call with a DIFFERENT body (remove card A then card B)
    // would be swallowed and reported with the first call's result. Double-click
    // protection belongs in the UI, where the payload is known.

    override async run(): Promise<ICorporateResponse | null> {
        const information = this.params?.information;
        const userData = getRecoilStateValue<IUserResponse>("userDataAtom");
        const corporateId = userData?.corporateId;

        if (!information || corporateId == null) {
            console.warn("[UpdateCorporateAction] Missing company or body; skipping.");
            return null;
        }

        // Guard the destructive case at the boundary as well as in the builder:
        // this PUT REPLACES rather than merges (measured on INT), so a body
        // missing address or contract erases them. It is guarded here even
        // though the request then 403s on the contract's presence — a failed
        // save is recoverable, an erased contract is not.
        if (!information.address || !information.contract) {
            console.error(
                "[UpdateCorporateAction] Refusing to PUT a body without address/contract."
            );
            showNotification("error", getTranslatedMessage("companyUpdateFailed"), "");
            return null;
        }

        try {
            const response: IApiResponse<ICorporateOk> = await updateCorporate(
                corporateId,
                information
            );

            if (response.status === "ok") {
                // PUT /corporates/{id} answers with the generic Ok envelope
                // ({ ok, id?, count? }) — NOT the corporate. Writing that
                // response into corporateDataAtom would blank the company's
                // ownerId, fail the admin check and eject the admin from the
                // section straight after a successful save.
                //
                // We sent the complete Corporate Information object, so on a
                // 200 the server state is exactly `information`: apply it
                // locally, and drop the session cache so the next cold read
                // still comes from the backend.
                getCorporateAction.invalidate();
                const current =
                    getRecoilStateValue<ICorporateResponse>("corporateDataAtom");
                const updated: ICorporateResponse | null = current
                    ? { ...current, data: information }
                    : null;
                if (updated) {
                    updateRecoilState("corporateDataAtom", updated);
                }
                showNotification(
                    "success",
                    getTranslatedMessage("companyUpdatedSuccessfully"),
                    getTranslatedMessage("companyUpdatedSuccessfullyDetail")
                );

                // Reconcile against the backend. The local apply above is a
                // safety net, not the source of truth: only a re-read picks up
                // anything the backend normalises or derives, refreshes
                // updatedAt, and reveals whether an omitted invoiceDetails was
                // cleared or preserved. GetCorporateAction keeps what we just
                // applied if this refresh fails.
                await getCorporateAction.execute();
                return (
                    getRecoilStateValue<ICorporateResponse>("corporateDataAtom") ?? updated
                );
            }

            // apiRequest surfaces its own notification for a failed request;
            // a second toast here would double up on the same failure.
            //
            // Verified on INT 2026-08-19: this currently ALWAYS fails with 403
            // "User is not allowed to update contract." The body carries
            // `contract` not because the schema requires it -- 2.0.5 requires
            // nothing -- but because this PUT replaces rather than merges, so
            // omitting it nulls the stored contract. A corporate
            // admin may not update a contract, and the check fires on the
            // field being present, not on it having changed (we sent it
            // unchanged). Until backend either lets an unchanged contract pass
            // or confirms it can be omitted without erasing it, Invoice
            // Details save and card removal cannot succeed. See row 19 of
            // docs/corporate-backend-requirements.csv.
            console.warn(
                "[UpdateCorporateAction] The company update failed. If this is a 403 " +
                "naming the contract, it is the known backend permission gap (row 19)."
            );
            return null;
        } catch (error: any) {
            console.error("[UpdateCorporateAction] Error updating the company:", error);
            showNotification("error", getTranslatedMessage("companyUpdateFailed"), "");
            return null;
        }
    }
}

export const updateCorporateAction = new UpdateCorporateAction();
